import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Play, Instagram, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../data/translations';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingBubbles from '../components/FloatingBubbles';

const ActualitesPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const videos = [
    {
      id: 1,
      youtubeId: 'KZb0bsYjCrQ',
      title: language === 'fr' 
        ? 'Installation photovoltaïque : Guide complet' 
        : 'Photovoltaic Installation: Complete Guide',
      description: language === 'fr'
        ? 'Découvrez notre guide complet sur l\'installation de panneaux solaires photovoltaïques pour votre maison ou entreprise.'
        : 'Discover our complete guide on installing solar photovoltaic panels for your home or business.',
      date: language === 'fr' ? 'Mars 2026' : 'March 2026'
    },
    {
      id: 2,
      youtubeId: 'bCRBY4BJsVc',
      title: language === 'fr'
        ? 'Maintenance de centrale photovoltaïque : Les bonnes pratiques'
        : 'Photovoltaic Plant Maintenance: Best Practices',
      description: language === 'fr'
        ? 'Que vous veniez de mettre en service votre centrale solaire ou que celle-ci fonctionne depuis plusieurs années, découvrez les bonnes pratiques de maintenance.'
        : 'Whether you have just commissioned your solar plant or it has been running for years, discover maintenance best practices.',
      date: language === 'fr' ? 'Mars 2026' : 'March 2026'
    },
    {
      id: 3,
      youtubeId: 'FJqOZwmtp0g',
      title: language === 'fr'
        ? 'Énergie solaire : Optimiser votre production'
        : 'Solar Energy: Optimize Your Production',
      description: language === 'fr'
        ? 'Apprenez comment optimiser la production énergétique de votre installation photovoltaïque pour un meilleur rendement.'
        : 'Learn how to optimize the energy production of your photovoltaic installation for better performance.',
      date: language === 'fr' ? 'Février 2026' : 'February 2026'
    },
    {
      id: 4,
      youtubeId: '-zDdHv9p-YY',
      title: language === 'fr'
        ? 'Construction durable : Nos projets récents'
        : 'Sustainable Construction: Our Recent Projects',
      description: language === 'fr'
        ? 'Explorez nos derniers projets de construction intégrant des solutions énergétiques durables et innovantes.'
        : 'Explore our latest construction projects integrating sustainable and innovative energy solutions.',
      date: language === 'fr' ? 'Février 2026' : 'February 2026'
    },
    {
      id: 5,
      youtubeId: '9XhivGQUYgY',
      title: language === 'fr'
        ? 'Rénovation énergétique : Témoignage client'
        : 'Energy Renovation: Customer Testimonial',
      description: language === 'fr'
        ? 'Découvrez le témoignage d\'un de nos clients sur la rénovation énergétique de son bâtiment avec installation photovoltaïque.'
        : 'Discover the testimonial of one of our clients on the energy renovation of their building with photovoltaic installation.',
      date: language === 'fr' ? 'Janvier 2026' : 'January 2026'
    },
    {
      id: 6,
      youtubeId: '9XhivGQUYgY',
      title: language === 'fr'
        ? 'Nos réalisations : Portfolio 2025-2026'
        : 'Our Achievements: Portfolio 2025-2026',
      description: language === 'fr'
        ? 'Retour sur nos principales réalisations en construction, rénovation et installation photovoltaïque de l\'année écoulée.'
        : 'Review of our main achievements in construction, renovation and photovoltaic installation from the past year.',
      date: language === 'fr' ? 'Janvier 2026' : 'January 2026'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-[240px]">
        {/* Hero Section */}
        <section className="relative h-[380px] md:h-[460px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets-wrfwihn1.emergentagent.net/job_construction-global/artifacts/k4xl1nsr_IMG_6331.jpg')",
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-black/25" />
          <div className="relative max-w-6xl mx-auto px-6 h-full flex flex-col justify-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors mb-4 self-start"
            >
              <ArrowLeft size={20} />
              <span className="font-semibold">{language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}</span>
            </button>
            <span className="inline-block text-orange-400 font-semibold tracking-widest text-sm mb-3">
              {language === 'fr' ? 'GLOBAL CONSTRUCTION & INGÉNIERIE' : 'GLOBAL CONSTRUCTION & INGÉNIERIE'}
            </span>
            <h1 className="text-white text-3xl sm:text-4xl md:text-5xl font-bold leading-tight max-w-3xl">
              {language === 'fr' ? 'Publications' : 'Publications'}
            </h1>
            <div className="mt-4 h-[3px] w-20 bg-orange-500 rounded-full" />
            <p className="text-white/85 mt-4 max-w-2xl text-base md:text-lg">
              {language === 'fr'
                ? 'Découvrez nos vidéos et actualités sur la construction, la rénovation et l\'énergie solaire'
                : 'Discover our videos and news on construction, renovation and solar energy'}
            </p>
          </div>
        </section>

        {/* Social CTA */}
        <section className="py-12 bg-white" data-testid="social-section">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-8">
              <span className="inline-block text-orange-500 font-semibold tracking-widest text-xs md:text-sm mb-2">
                {language === 'fr' ? 'SUIVEZ-NOUS' : 'FOLLOW US'}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#702963' }}>
                {language === 'fr' ? 'Retrouvez GCI sur les réseaux sociaux' : 'Find GCI on social media'}
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-3xl mx-auto">
              {/* Instagram */}
              <a
                href="https://www.instagram.com/gci_rdc"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
                data-testid="social-instagram-btn"
                style={{ background: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
              >
                <div className="relative flex items-center gap-4 p-5 md:p-6 text-white">
                  <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-full bg-white/20 backdrop-blur">
                    <Instagram className="w-7 h-7" strokeWidth={2} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-widest opacity-85">Instagram</div>
                    <div className="text-lg md:text-xl font-bold">@gci_rdc</div>
                  </div>
                  <ArrowUpRight className="w-6 h-6 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
              </a>

              {/* TikTok */}
              <a
                href="https://www.tiktok.com/@globalconstructio1"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 bg-black"
                data-testid="social-tiktok-btn"
              >
                <div className="relative flex items-center gap-4 p-5 md:p-6 text-white">
                  <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center rounded-full bg-white/10 backdrop-blur">
                    <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor" aria-hidden="true">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.87a8.16 8.16 0 0 0 4.77 1.52V6.94a4.85 4.85 0 0 1-1.84-.25z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-widest opacity-85">TikTok</div>
                    <div className="text-lg md:text-xl font-bold">@globalconstructio1</div>
                  </div>
                  <ArrowUpRight className="w-6 h-6 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </div>
                {/* subtle neon accent */}
                <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ boxShadow: 'inset 0 0 40px rgba(37, 244, 238, 0.25), inset 0 0 40px rgba(254, 44, 85, 0.2)' }} />
              </a>
            </div>
          </div>
        </section>

        {/* Videos Section */}
        <section className="py-12 bg-gray-50 relative overflow-hidden">
          <FloatingBubbles count={15} />
          <div className="max-w-[1800px] mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {videos.map((video) => (
                <article
                  key={video.id}
                  className="group bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  {/* YouTube Video Embed */}
                  <div className="relative aspect-video overflow-hidden bg-black">
                    <iframe
                      src={`https://www.youtube.com/embed/${video.youtubeId}?rel=0`}
                      title={video.title}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-full"
                    />
                    {/* Badge Orange */}
                    <div className="absolute top-4 right-4 bg-orange-500 text-white p-3 rounded-lg shadow-lg">
                      <Play size={24} fill="white" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    {/* Date */}
                    <div className="flex items-center gap-2 text-gray-500 text-base mb-3">
                      <Calendar size={16} />
                      <span>{video.date}</span>
                    </div>
                    
                    {/* Title with Orange Background */}
                    <div className="bg-orange-500 text-white px-6 py-3 rounded-lg mb-4 -mx-2">
                      <h3 className="text-2xl font-bold leading-tight">
                        {video.title}
                      </h3>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed">
                      {video.description}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ActualitesPage;
